import { dropTask } from 'ember-concurrency';
import { PhDeviceMobile, PhPaperPlaneTilt, PhWarningCircle } from 'ember-phosphor-icons';
import {
  checkPhoneVerificationRequest,
  parsePhoneVerificationResult,
  phoneVerificationAttempt,
  phoneVerificationErrorCode,
  startPhoneVerificationRequest,
  submitPhoneVerificationRequest,
} from 'tio-data/requests/phone-number';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import CodeInput from 'tio-ui/components/code-input';
import Component from '@glimmer/component';
import Heading from 'tio-ui/components/heading';
import matchMedia from 'tio-common/helpers/match-media';
import IconBadge from 'tio-ui/components/icon-badge';
import PhoneInput from 'tio-ui/components/phone-input';
import type IntlService from 'ember-intl/services/intl';
import type {
  PhoneVerificationAttemptPayload,
  PhoneVerificationCheckResult,
  PhoneVerificationPlatform,
  PhoneVerificationResult,
  PhoneVerificationSubmitResult,
  PhoneVerificationSurface,
} from 'tio-data/requests/phone-number';
import type { PhoneCountryCode, PhoneMeta } from 'tio-ui/components/phone-input';
import type { Store as StoreService } from '@warp-drive/core';

// ---------------------------------------------------------------------------
// PhoneVerificationFlow – one implementation of the retry/lockout rules
// ---------------------------------------------------------------------------

export type FlowStep = 'enter-number' | 'code' | 'verified' | 'deferred';

/** Where a surface may open the flow. Terminal steps are reached, never entered. */
export type FlowEntryStep = Extract<FlowStep, 'enter-number' | 'code'>;
export type FlowSize = 'sm' | 'md' | 'lg';

/** Matches CodeInput's default; the server accepts 4-10 but the UI issues six. */
const CODE_LENGTH = 6;

/** The five RFC 110 surfaces, sent with each request so the funnel is attributed. */
export type FlowSurface = PhoneVerificationSurface;

/**
 * The only refusal that resolves to verify-later. `rate_limit_exceeded` is the resend
 * cooldown and the abuse limiter — a wait, so it keeps the member on the step.
 */
const LOCKOUT_ERROR_CODE = 'verification_locked_out';

/**
 * The retry/cooldown/lockout facts the server owns. Every one stays optional: a
 * response that omits them degrades to "no countdown shown" rather than inventing one.
 * Nothing here is ever hardcoded — a client constant would drift from Twilio Verify's
 * hosted TTL the moment either changed.
 */
export type VerificationMeta = PhoneVerificationAttemptPayload;

/**
 * The interval lives in a modifier so Ember tears it down with the element; a
 * class-held timer outlives a flow that unmounts mid-countdown.
 */
const ticking = modifier((_element: HTMLElement, [onTick]: [() => void]) => {
  const id = setInterval(onTick, 1000);

  return () => clearInterval(id);
});

export interface PhoneVerificationFlowSignature {
  Args: {
    /** Prefills the number step; omit to collect one. */
    phoneNumber?: string;
    initialStep?: FlowEntryStep;
    size?: FlowSize;
    /** Attribution only — the component emits no analytics of its own. */
    surface: FlowSurface;
    onVerified?: (result: PhoneVerificationCheckResult) => void;
    /** Fired when the flow resolves to verify-later, including on lockout. */
    onDeferred?: () => void;
    onChangeNumber?: () => void;
    onCancel?: () => void;
    /** Per-surface confirmation copy; only enrollment's is designed today. */
    confirmationTitle?: string;
    confirmationBody?: string;
  };
  Element: HTMLDivElement;
}

export default class PhoneVerificationFlow extends Component<PhoneVerificationFlowSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked private stepOverride?: FlowStep;
  /** Undefined until the member touches the field, so a supplied number can prefill. */
  @tracked private numberInput?: string;
  /** E.164 from @onValidChange, or null while the input cannot be dialled. */
  @tracked private numberE164: string | null = null;
  /** The country selected alongside it; `+1` alone cannot say US or CA. */
  @tracked private numberCountry?: PhoneCountryCode;
  @tracked code = '';
  @tracked errorMessage = '';
  @tracked private meta: VerificationMeta = {};
  @tracked private codeRejected = false;
  /** Re-read on every tick so the countdown getters recompute; see `ticking`. */
  @tracked private now = Date.now();

  /**
   * Entry is number-first unless a caller explicitly resumes at `code`. A known
   * `@phoneNumber` only prefills the field: defaulting to the code step rendered "We
   * sent a code" for a challenge this flow had never started, since nothing but the
   * send button and resend perform one.
   */
  get step(): FlowStep {
    return this.stepOverride ?? this.args.initialStep ?? 'enter-number';
  }

  /** Passed to CodeInput so the boxes are announced invalid, not just the banner. */
  get codeErrors(): string[] {
    return this.errorMessage ? [this.errorMessage] : [];
  }

  /** The code step submits a full code only; a short one would spend a server attempt. */
  get isCodeIncomplete(): boolean {
    return this.code.length < CODE_LENGTH;
  }

  /** The media query the mobile default resolves against, matching Tailwind's `md`. */
  desktopQuery = '(min-width: 768px)';

  /**
   * What the field shows. Once touched it is whatever the member typed — including
   * empty — so backspacing a prefilled number does not snap it back and "Change
   * number" does not re-offer the number they just rejected.
   */
  get displayNumber(): string {
    return this.numberInput ?? this.args.phoneNumber ?? '';
  }

  /**
   * What gets sent. E.164 only: PhoneInput's @onChange reports the *formatted national*
   * string, and the server prepends +1 to any ten digits — so posting that would send a
   * Mexican number's SMS to a US number and store the wrong one as the member's mobile.
   */
  get submitNumber(): string | undefined {
    if (this.numberInput === undefined) {
      return this.args.phoneNumber;
    }

    return this.numberE164 ?? undefined;
  }

  get attemptsRemaining(): number | undefined {
    return this.meta.attemptsRemaining;
  }

  /**
   * The server sends attemptsRemaining on every response, so this gates on a rejection
   * having happened — a warning about a failure that has not is a threat.
   */
  get shouldWarnAttempts(): boolean {
    return this.codeRejected && (this.attemptsRemaining ?? 0) > 0;
  }

  /**
   * Locked out when the server says so. Absent means not locked — never assume a
   * lockout the server has not declared.
   */
  get isLockedOut(): boolean {
    return this.lockoutSecondsRemaining > 0;
  }

  /** Whole seconds until the server will accept another send; 0 when resend is free. */
  get resendSecondsRemaining(): number {
    return this.secondsUntil(this.meta.resendAvailableAt);
  }

  get lockoutSecondsRemaining(): number {
    return this.secondsUntil(this.meta.lockedUntil);
  }

  get codeExpirySecondsRemaining(): number {
    return this.secondsUntil(this.meta.codeExpiresAt);
  }

  get codeExpiryDisplay(): string {
    return PhoneVerificationFlow.asClock(this.codeExpirySecondsRemaining);
  }

  get lockoutDisplay(): string {
    return PhoneVerificationFlow.asClock(this.lockoutSecondsRemaining);
  }

  get isResendBlocked(): boolean {
    return this.resendSecondsRemaining > 0;
  }

  /** Minutes and seconds, because these windows are ten and fifteen minutes long. */
  private static asClock(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);

    return `${minutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
  }

  /** Rounds up, so a countdown never displays 0 while the cooldown is still in force. */
  private secondsUntil(timestamp?: string): number {
    if (!timestamp) {
      return 0;
    }

    const remaining = new Date(timestamp).getTime() - this.now;

    return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
  }

  get isSubmitDisabled(): boolean {
    return !this.submitNumber || this.sendToNumber.isRunning || this.isResendBlocked;
  }

  /**
   * A code-step request is in flight. The actions there are mutually exclusive: a
   * resend landing after a successful check would reset the step to `code`, and
   * deferring mid-check fires both callbacks for one flow.
   */
  get isBusy(): boolean {
    return this.submitCode.isRunning || this.resend.isRunning;
  }

  get isVerifyDisabled(): boolean {
    return this.isCodeIncomplete || this.isBusy;
  }

  get isResendDisabled(): boolean {
    return this.isBusy || this.isResendBlocked;
  }

  /**
   * The code step announces failures through the field, which also carries
   * `aria-invalid` and the error association. Repeating them in the banner's live
   * region would announce one rejected code twice.
   */
  get bannerMessage(): string {
    return this.isStep('code') ? '' : this.errorMessage;
  }

  /** Template-friendly step predicate, so each branch reads as its own name. */
  isStep = (step: FlowStep): boolean => this.step === step;

  /**
   * Read at request time rather than tracked: the funnel wants the viewport the member
   * actually acted in, and a mid-flow resize should not retag the attempt.
   */
  private get platform(): PhoneVerificationPlatform {
    return window.matchMedia(this.desktopQuery).matches ? 'desktop' : 'mobile';
  }

  /** Attribution attached to every command, so the funnel can group by surface. */
  private get origin() {
    return { surface: this.args.surface, platform: this.platform };
  }

  /**
   * Persists the number as `unverified` *and* sends the first code. Submitting rather
   * than only starting is what makes abandoning the code step survivable — the number
   * is on file, so the deferred link has somewhere to point.
   */
  sendToNumber = dropTask(async () => {
    const number = this.submitNumber;
    if (!number) {
      return;
    }

    this.errorMessage = '';
    try {
      const { content } = await this.store.request(
        submitPhoneVerificationRequest(number, { ...this.origin, country: this.numberCountry })
      );
      this.applyMeta(parsePhoneVerificationResult<PhoneVerificationSubmitResult>(content));
      this.stepOverride = 'code';
      this.code = '';
    } catch (error: unknown) {
      this.handleRefusal(error);
    }
  });

  submitCode = dropTask(async (code?: string) => {
    const submitted = code ?? this.code;
    this.errorMessage = '';

    try {
      const { content } = await this.store.request(
        checkPhoneVerificationRequest(submitted, this.submitNumber, this.origin)
      );
      const result = parsePhoneVerificationResult<PhoneVerificationCheckResult>(content);
      this.applyMeta(result);
      this.stepOverride = 'verified';
      this.args.onVerified?.(result);
    } catch (error: unknown) {
      this.handleFailedCode(error);
    }
  });

  /** Re-sends to the number already on file; the number step owns storing it. */
  resend = dropTask(async () => {
    this.code = '';
    this.errorMessage = '';

    try {
      const { content } = await this.store.request(
        startPhoneVerificationRequest(this.submitNumber, this.origin)
      );
      this.applyMeta(parsePhoneVerificationResult<PhoneVerificationResult>(content));
    } catch (error: unknown) {
      this.handleRefusal(error);
    }
  });

  /**
   * Countdown and attempt figures come from the server on every response, so a
   * response that omits `verification` clears stale numbers instead of freezing them.
   */
  private applyMeta(result: PhoneVerificationResult) {
    this.meta = result.verification ?? {};
    this.now = Date.now();
  }

  tick = () => {
    this.now = Date.now();
  };

  private handleFailedCode(error: unknown) {
    this.code = '';
    this.codeRejected = true;
    this.handleRefusal(error);
  }

  /**
   * Adopts the countdowns the rejection carries, so a refused resend cannot re-enable on
   * the previous response's cooldown. Defers only once the attempts are spent.
   */
  private handleRefusal(error: unknown) {
    this.errorMessage = this.messageFor(error);

    const attempt = phoneVerificationAttempt(error);
    if (attempt) {
      this.meta = attempt;
      this.now = Date.now();
    }

    if (phoneVerificationErrorCode(error) === LOCKOUT_ERROR_CODE || this.lockoutReached) {
      this.deferVerification();
    }
  }

  /** True only once the server tells us; absent countdown data never implies a lockout. */
  private get lockoutReached(): boolean {
    return this.isLockedOut || this.attemptsRemaining === 0;
  }

  deferVerification = () => {
    // The confirmation is a way forward, so it must not carry the failure banner.
    this.errorMessage = '';
    this.stepOverride = 'deferred';
    this.args.onDeferred?.();
  };

  cancel = () => {
    this.args.onCancel?.();
  };

  changeNumber = () => {
    this.code = '';
    this.codeRejected = false;
    this.errorMessage = '';
    this.numberInput = '';
    this.numberE164 = null;
    this.numberCountry = undefined;
    this.stepOverride = 'enter-number';
    this.args.onChangeNumber?.();
  };

  /** Keystrokes: display only. The dialable value arrives via onValidChange on blur. */
  updateNumber = (value: string) => {
    this.numberInput = value;
  };

  /**
   * Records the SELECTED country alongside the E.164 value. `+1` is shared between the
   * US and Canada and cannot be split by area code, so the selection is the only thing
   * that tells the server which country a NANP number belongs to.
   */
  updateValidNumber = (e164: string | null, meta?: PhoneMeta) => {
    this.numberE164 = e164;
    this.numberCountry = meta?.country;
  };

  updateCode = (value: string) => {
    this.code = value;
  };

  /** The button submits whatever is typed; CodeInput's onComplete passes the code. */
  submitTypedCode = () => {
    this.submitCode.perform();
  };

  sendCode = () => {
    this.sendToNumber.perform();
  };

  resendCode = () => {
    this.resend.perform();
  };

  /**
   * Localised from the server's error `code`, never its `detail` — the detail is
   * English prose written for an operator.
   */
  private messageFor(error: unknown): string {
    const code = phoneVerificationErrorCode(error);

    return code
      ? this.intl.t(`phone_verification.errors.${code}`)
      : this.intl.t('phone_verification.errors.unknown');
  }

  <template>
    {{! An omitted @size resolves per viewport rather than always rendering the desktop
        tier; match-media is the repository's mechanism and re-renders on change. }}
    {{#let (if @size @size (if (matchMedia this.desktopQuery) "md" "sm")) as |size|}}
      <div class="tio-col-loose" data-test-phone-verification-flow={{@surface}} ...attributes>
        {{#if this.bannerMessage}}
          <AlertBanner @intent="danger" data-test-flow-error>
            <PhWarningCircle class="inline size-4" />
            {{this.bannerMessage}}
          </AlertBanner>
        {{/if}}

        {{#if (this.isStep "enter-number")}}
          <IconBadge @icon={{PhDeviceMobile}} @color="teal" @size="lg" />
          <Heading @level={{2}} @size="lg">{{t "phone_verification.number.title"}}</Heading>
          <p class="tio-text-sm-normal">{{t "phone_verification.number.consent"}}</p>

          <PhoneInput
            @testHook="phone-verification-number"
            @value={{this.displayNumber}}
            @size={{size}}
            @onChange={{this.updateNumber}}
            @onValidChange={{this.updateValidNumber}}
            @isDisabled={{this.sendToNumber.isRunning}}
            @label={{t "phone_verification.number.label"}}
          />

          <Button
            @intent="primary"
            @isRunning={{this.sendToNumber.isRunning}}
            disabled={{this.isSubmitDisabled}}
            {{on "click" this.sendCode}}
            data-test-send-code
          >{{t "phone_verification.number.send"}}</Button>
        {{/if}}

        {{#if (this.isStep "code")}}
          <IconBadge @icon={{PhDeviceMobile}} @color="teal" @size="lg" />
          <Heading @level={{2}} @size="lg">{{t "phone_verification.code.title"}}</Heading>
          <p class="tio-text-sm-normal">
            {{t "phone_verification.code.sent_to"}}
            <strong data-test-code-destination>{{this.submitNumber}}</strong>
          </p>

          <Button
            @appearance="minimal"
            @size="sm"
            disabled={{this.isBusy}}
            {{on "click" this.changeNumber}}
            data-test-change-number
          >{{t "phone_verification.code.change_number"}}</Button>

          <CodeInput
            @testHook="phone-verification-code"
            @value={{this.code}}
            @size={{size}}
            @onChange={{this.updateCode}}
            @onComplete={{this.submitCode.perform}}
            @errors={{this.codeErrors}}
            @isDisabled={{this.isBusy}}
            @label={{t "phone_verification.code.label"}}
          />

          {{! One interval drives every countdown in this step. }}
          <div class="tio-col-flush" {{ticking this.tick}}>
            {{#if this.shouldWarnAttempts}}
              <p class="tio-text-sm-normal" data-test-attempts-remaining>
                {{t "phone_verification.code.attempts_left" count=this.attemptsRemaining}}
              </p>
            {{/if}}

            {{#if this.codeExpirySecondsRemaining}}
              <p class="tio-text-sm-normal" data-test-phone-verification-code-expiry>
                {{t "phone_verification.code.expires_in" time=this.codeExpiryDisplay}}
              </p>
            {{/if}}

            {{#if this.isResendBlocked}}
              <p class="tio-text-sm-normal" data-test-phone-verification-resend-countdown>
                {{t "phone_verification.code.resend_in" seconds=this.resendSecondsRemaining}}
              </p>
            {{/if}}
          </div>

          <Button
            @intent="primary"
            @isRunning={{this.submitCode.isRunning}}
            disabled={{this.isVerifyDisabled}}
            {{on "click" this.submitTypedCode}}
            data-test-verify
          >{{t "phone_verification.code.verify"}}</Button>

          <Button
            @appearance="minimal"
            @isRunning={{this.resend.isRunning}}
            disabled={{this.isResendDisabled}}
            {{on "click" this.resendCode}}
            data-test-phone-verification-resend
          >{{t "phone_verification.code.resend"}}</Button>

          <hr />
          <Button
            @appearance="minimal"
            @size="sm"
            disabled={{this.isBusy}}
            {{on "click" this.deferVerification}}
            data-test-verify-later
          >{{t "phone_verification.code.verify_later"}}</Button>
        {{/if}}

        {{#if (this.isStep "verified")}}
          <IconBadge @icon={{PhDeviceMobile}} @color="teal" @size="lg" />
          <Heading @level={{2}} @size="lg" data-test-verified>{{t
              "phone_verification.verified.title"
            }}</Heading>
          <p class="tio-text-sm-normal">{{t "phone_verification.verified.body"}}</p>
        {{/if}}

        {{#if (this.isStep "deferred")}}
          <IconBadge @icon={{PhPaperPlaneTilt}} @color="purple" @size="lg" />
          <Heading @level={{2}} @size="lg" data-test-deferred>
            {{if @confirmationTitle @confirmationTitle (t "phone_verification.deferred.title")}}
          </Heading>
          <p class="tio-text-sm-normal">
            {{if @confirmationBody @confirmationBody (t "phone_verification.deferred.body")}}
          </p>
          <AlertBanner @intent="warning">{{t
              "phone_verification.deferred.unverified_warning"
            }}</AlertBanner>

          {{#if this.isLockedOut}}
            <p
              class="tio-text-sm-normal"
              data-test-phone-verification-lockout-countdown
              {{ticking this.tick}}
            >
              {{t "phone_verification.deferred.locked_for" time=this.lockoutDisplay}}
            </p>
          {{/if}}
        {{/if}}
      </div>
    {{/let}}
  </template>
}
